import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getClient,
  getTrends,
  getComparison,
  getCampaigns,
  getPlatforms,
  getSubscription,
  generateAIInsights,
  getAIInsights,
  getReportHistory,
  getEmailSettings,
  saveEmailSettings,
  sendTestEmail,
  sendMonthlyReport,
  getGmailConnectionStatus,
  connectGmail,
  disconnectGmail,
} from '../utils/api';

import { MetricCard } from '../components/MetricCard';

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ArrowLeft, TrendingUp, DollarSign, MousePointerClick, Target, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getReportMetricConfig,
  getMetricFormatter,
  isPositiveChange,
} from '../utils/reportMetricConfig';


const COLORS = ['#2563EB', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2'];
const PLATFORMS = ['all', 'meta', 'google', 'linkedin', 'twitter', 'tiktok', 'other'];
// Email Reports is temporarily hidden from users.
// Set this to true after Google OAuth verification/setup is ready for production.
const EMAIL_REPORTS_ENABLED = false;

const fmt = (n, d = 0) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'د.إ',
  SGD: 'S$',
};

const fmtCur = (n, currency = 'INR') =>
  `${CURRENCY_SYMBOLS[currency] || currency} ${fmt(n, 2)}`;
const fmtPct = (n) => `${fmt(n, 2)}%`;


const Tooltip_ = ({ active, payload, label, prefix = '', suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', boxShadow: 'var(--shadow-md)' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 13, fontWeight: 600, color: p.color }}>
          {p.name}: {prefix}{fmt(p.value, 2)}{suffix}
        </div>
      ))}
    </div>
  );
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
const [emailSettings, setEmailSettings] = useState({
  enabled: false,
  recipient_email: '',
  cc_email: '',
  send_day: 1,
});
const [savingEmailSettings, setSavingEmailSettings] = useState(false);
const [sendingTestEmail, setSendingTestEmail] = useState(false);
const [sendingMonthlyEmail, setSendingMonthlyEmail] = useState(false);
const [gmailConnection, setGmailConnection] = useState({ connected: false, email: null, provider: 'smtp' });
const [gmailConnecting, setGmailConnecting] = useState(false);
  const [trends, setTrends] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('all');
  const [reportCurrency, setReportCurrency] = useState('INR');
  const [activeTab, setActiveTab] = useState('overview');
const [subscription, setSubscription] = useState(null);
const [aiInsight, setAiInsight] = useState(null);
const [aiLoading, setAiLoading] = useState(false);
const planName = String(
  subscription?.plan_name ||
  subscription?.planName ||
  subscription?.plan ||
  'free'
).toLowerCase();
const canUseEmailReports = EMAIL_REPORTS_ENABLED && ['pro', 'agency'].includes(planName);

  const load = useCallback(async () => {
    setLoading(true);

    const params = {
      platform: platform !== 'all' ? platform : undefined,
    };

    try {
      const clientData = await getClient(id);
      setClient(clientData);



     const trendsData = await getTrends(id, { ...params, months: 6 });
     const byMonth = {};

     trendsData.forEach(row => {
       if (!byMonth[row.month]) {
         byMonth[row.month] = {
           month: row.month,
           spend: 0,
           clicks: 0,
           conversions: 0,
           impressions: 0,
           revenue: 0,
           roas: 0,
           orders: 0,
           quantity: 0,
           profit: 0,
           report_type: row.report_type,
         };
       }

       byMonth[row.month].spend += parseFloat(row.spend || 0);
       byMonth[row.month].clicks += parseFloat(row.clicks || 0);
       byMonth[row.month].conversions += parseFloat(row.conversions || 0);
       byMonth[row.month].impressions += parseFloat(row.impressions || 0);
       byMonth[row.month].revenue += parseFloat(row.revenue || 0);
       byMonth[row.month].orders += parseFloat(row.orders || 0);
       byMonth[row.month].quantity += parseFloat(row.quantity || 0);
       byMonth[row.month].profit += parseFloat(row.profit || 0);
       byMonth[row.month].roas =
         byMonth[row.month].spend > 0
           ? byMonth[row.month].revenue / byMonth[row.month].spend
           : 0;
     });

      setTrends(
        Object.values(byMonth).sort((a, b) =>
          a.month.localeCompare(b.month)
        )
      );

      const comparisonData = await getComparison(id, params);
      setComparison(comparisonData);

      const campaignData = await getCampaigns(id, params);
      setCampaigns(campaignData);

      const platformData = await getPlatforms(id, params);
      setPlatforms(platformData);

      const reportHistory = await getReportHistory(id);
      const latestCurrency = reportHistory?.[0]?.currency || 'INR';
      setReportCurrency(latestCurrency);

      getSubscription()
        .then(setSubscription)
        .catch(() => setSubscription(null));

      getAIInsights(id)
        .then(setAiInsight)
        .catch(() => setAiInsight(null));
    } catch (error) {
      console.error('Client detail load error:', error.response?.data || error.message);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [id, platform]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!id || !canUseEmailReports) return;

    getEmailSettings(id)
      .then(setEmailSettings)
      .catch(() => {});

    getGmailConnectionStatus()
      .then(setGmailConnection)
      .catch(() => setGmailConnection({ connected: false, email: null, provider: 'smtp' }));
  }, [id, canUseEmailReports]);

  useEffect(() => {
    if (activeTab === 'email reports' && !canUseEmailReports) {
      setActiveTab('overview');
    }
  }, [activeTab, canUseEmailReports]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailStatus = params.get('gmail');

    if (!gmailStatus) return;

    if (gmailStatus === 'connected') {
      toast.success('Gmail connected successfully');
      if (canUseEmailReports) {
        getGmailConnectionStatus()
          .then(setGmailConnection)
          .catch(() => {});
      }
    } else if (gmailStatus === 'failed') {
      toast.error('Failed to connect Gmail');
    }

    params.delete('gmail');
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [canUseEmailReports]);

  const platformData = platforms.map(p => ({ name: p.platform, value: parseFloat(p.spend || 0) }));

const totalPlatformSpend = platforms.reduce(
  (sum, p) => sum + parseFloat(p.spend || 0),
  0
);

const topPlatform = platforms.length
  ? [...platforms].sort(
      (a, b) => parseFloat(b.spend || 0) - parseFloat(a.spend || 0)
    )[0]
  : null;
const totalCampaignSpend = campaigns.reduce(
  (sum, c) => sum + parseFloat(c.spend || 0),
  0
);

const currentMonthMetrics = comparison?.comparison || {};
const hasAggregateMetric = (key) =>
  currentMonthMetrics?.[key]?.current !== undefined &&
  currentMonthMetrics?.[key]?.current !== null;
const campaignSummarySpend = hasAggregateMetric('spend')
  ? Number(currentMonthMetrics.spend.current || 0)
  : totalCampaignSpend;
const campaignSummaryConversions = hasAggregateMetric('conversions')
  ? Number(currentMonthMetrics.conversions.current || 0)
  : campaigns.reduce(
      (sum, c) => sum + Number(c.conversions || c.orders || 0),
      0
    );
const campaignSummaryRevenue = hasAggregateMetric('revenue')
  ? Number(currentMonthMetrics.revenue.current || 0)
  : campaigns.reduce((sum, c) => sum + Number(c.revenue || 0), 0);
const campaignSummaryRoas =
  campaignSummarySpend > 0 && campaignSummaryRevenue > 0
    ? campaignSummaryRevenue / campaignSummarySpend
    : 0;

const detectedReportType =
  comparison?.reportType ||
  comparison?.report_type ||
  campaigns?.[0]?.report_type ||
  platforms?.[0]?.report_type ||
  trends?.[0]?.report_type ||
  'needs_review';

const normalizeFrontendReportType = (type) => {
  if (['sales_campaign', 'lead_generation', 'sales_data', 'traffic', 'engagement', 'app', 'acquisition'].includes(type)) {
    return type;
  }

  // backward compatibility for old uploaded rows
  if (type === 'ads') {
    const revenue = Number(currentMonthMetrics?.revenue?.current || 0);
    return revenue > 0 ? 'sales_campaign' : 'lead_generation';
  }

  return 'needs_review';
};

const normalizedReportType = (() => {
  const normalized = normalizeFrontendReportType(detectedReportType);

  const revenue =
    Number(currentMonthMetrics?.revenue?.current || 0) ||
    trends.reduce((sum, r) => sum + Number(r.revenue || 0), 0) ||
    campaigns.reduce((sum, c) => sum + Number(c.revenue || 0), 0) ||
    platforms.reduce((sum, p) => sum + Number(p.revenue || 0), 0);

  const conversions =
    Number(currentMonthMetrics?.conversions?.current || 0) ||
    trends.reduce((sum, r) => sum + Number(r.conversions || 0), 0) ||
    campaigns.reduce((sum, c) => sum + Number(c.conversions || 0), 0) ||
    platforms.reduce((sum, p) => sum + Number(p.conversions || 0), 0);

  if (revenue > 0 && conversions > 0) return 'sales_campaign';

  if (normalized !== 'needs_review') return normalized;

  return 'lead_generation';
})();
const metricConfig = getReportMetricConfig(normalizedReportType);

const isDashboardMetricAvailable = (key) => {
  const metric = currentMonthMetrics?.[key];
  if (metric && Object.prototype.hasOwnProperty.call(metric, 'available')) {
    return metric.available !== false;
  }

  return true;
};

const visibleOverviewCards = metricConfig.overviewCards.filter(isDashboardMetricAvailable);
const hasRevenueMetric = isDashboardMetricAvailable('revenue');
const hasRoasMetric = isDashboardMetricAvailable('roas');
const hasClickMetrics = isDashboardMetricAvailable('clicks');
const hasCtrMetric = isDashboardMetricAvailable('ctr');
const hasCpcMetric = isDashboardMetricAvailable('cpc');


const fmtReportCur = (value) => fmtCur(value, reportCurrency);
const currencySymbol =
  CURRENCY_SYMBOLS[reportCurrency] || reportCurrency;

const formatMetric = (key, value) =>
  getMetricFormatter(key, { fmt, fmtCur: fmtReportCur, fmtPct })(value);

const getCurrentValue = (key) =>
  currentMonthMetrics?.[key]?.current ?? 0;

  const getCampaignColumns = () => {
    if (normalizedReportType === 'sales_campaign') {
      return [
        { key: 'name', label: 'Campaign', format: (c) => c.name || c.campaign_name || 'Unknown' },
        { key: 'spend', label: 'Spend', format: (c) => fmtReportCur(c.spend) },
        { key: 'conversions', label: metricConfig.primaryMetricLabel, format: (c) => fmt(c.conversions) },
        { key: 'cpa',label: metricConfig.costMetricLabel, format: (c) =>fmtReportCur(c.cpa) },
        hasRevenueMetric ? { key: 'revenue', label: 'Revenue', format: (c) => fmtReportCur(c.revenue) } : null,
        hasRoasMetric ? { key: 'roas', label: 'Return', format: (c) => `${fmt(c.roas, 2)}x` } : null,
      ].filter(Boolean);
    }

    if (normalizedReportType === 'lead_generation') {
      return [
        { key: 'name', label: 'Campaign', format: (c) => c.name || c.campaign_name || 'Unknown' },
        { key: 'spend', label: 'Spend', format: (c) => fmtReportCur(c.spend) },
        { key: 'conversions',label: metricConfig.primaryMetricLabel, format: (c) => fmt(c.conversions) },
        { key: 'cpa', label: metricConfig.costMetricLabel, format: (c) => fmtReportCur(c.cpa) },
        hasCtrMetric ? { key: 'ctr', label: 'Click Rate', format: (c) => fmtPct(c.ctr) } : null,
        hasCpcMetric ? { key: 'cpc', label: 'Cost / Click', format: (c) => fmtReportCur(c.cpc) } : null,
      ].filter(Boolean);
    }

    if (normalizedReportType === 'sales_data') {
      return [
        { key: 'name', label: 'Product', format: (c) => c.name || c.campaign_name || 'Unknown' },
        { key: 'orders', label: 'Orders', format: (c) => fmt(c.orders) },
        { key: 'quantity', label: 'Qty Sold', format: (c) => fmt(c.quantity) },
        { key: 'revenue', label: 'Revenue', format: (c) => fmtReportCur(c.revenue) },
        { key: 'profit', label: 'Profit', format: (c) => fmtReportCur(c.profit) },
        { key: 'margin', label: 'Margin', format: (c) => fmtPct(c.margin) },
      ];
    }

    return [
      { key: 'name', label: 'Campaign', format: (c) => c.name || c.campaign_name || 'Unknown' },
      { key: 'spend', label: 'Spend', format: (c) => fmtReportCur(c.spend) },
      { key: 'clicks', label: 'Clicks', format: (c) => fmt(c.clicks) },
      { key: 'conversions', label: 'Results', format: (c) => fmt(c.conversions) },
      { key: 'cpa', label: 'Cost / Result', format: (c) => fmtReportCur(c.cpa) },
    ];
  };

  const campaignColumns = getCampaignColumns();
  const getPlatformColumns = () => {
    if (normalizedReportType === 'sales_campaign') {
      return [
        { key: 'platform', label: 'Platform', format: (p) => p.platform || 'N/A' },
        { key: 'spend', label: 'Spend', format: (p) => fmtReportCur(p.spend) },
        { key: 'conversions', label: metricConfig.primaryMetricLabel, format: (p) => fmt(p.conversions) },
        { key: 'cpa',label: metricConfig.costMetricLabel, format: (p) => fmtReportCur(p.cpa) },
        { key: 'revenue', label: 'Revenue', format: (p) => fmtReportCur(p.revenue) },
        { key: 'roas', label: 'ROAS', format: (p) => `${fmt(p.roas, 2)}x` },
      ];
    }

    if (normalizedReportType === 'lead_generation') {
      return [
        { key: 'platform', label: 'Platform', format: (p) => p.platform || 'N/A' },
        { key: 'spend', label: 'Spend', format: (p) => fmtReportCur(p.spend) },
        { key: 'conversions',label: metricConfig.primaryMetricLabel, format: (p) => fmt(p.conversions) },
        { key: 'cpa', label: metricConfig.costMetricLabel, format: (p) => fmtReportCur(p.cpa) },
        hasCtrMetric ? { key: 'ctr', label: 'CTR', format: (p) => fmtPct(p.ctr) } : null,
        hasCpcMetric ? { key: 'cpc', label: 'CPC', format: (p) => fmtReportCur(p.cpc) } : null,
      ].filter(Boolean);
    }

    if (normalizedReportType === 'sales_data') {
      return [
        { key: 'platform', label: 'Channel', format: (p) => p.platform || 'N/A' },
        { key: 'orders', label: 'Orders', format: (p) => fmt(p.orders) },
        { key: 'quantity', label: 'Qty Sold', format: (p) => fmt(p.quantity) },
        { key: 'revenue', label: 'Revenue', format: (p) => fmtReportCur(p.revenue) },
        { key: 'profit', label: 'Profit', format: (p) => fmtReportCur(p.profit) },
      ];
    }

    return [
      { key: 'platform', label: 'Platform', format: (p) => p.platform || 'N/A' },
      { key: 'spend', label: 'Spend', format: (p) => fmtReportCur(p.spend) },
      { key: 'clicks', label: 'Clicks', format: (p) => fmt(p.clicks) },
      { key: 'conversions', label: 'Conversions', format: (p) => fmt(p.conversions) },
      { key: 'cpa', label: 'CPA', format: (p) => fmtReportCur(p.cpa) },
    ];
  };

const topCampaign = campaigns.length
  ? [...campaigns].sort((a, b) => {
      if (normalizedReportType === 'sales_data') {
        return Number(b.profit || 0) - Number(a.profit || 0);
      }

      if (normalizedReportType === 'lead_generation') {
        return Number(b.conversions || 0) - Number(a.conversions || 0);
      }

      return Number(b.roas || 0) - Number(a.roas || 0);
    })[0]
  : null;

const lowCampaign = campaigns.length
  ? [...campaigns].sort((a, b) => {
      if (normalizedReportType === 'sales_data') {
        return Number(a.profit || 0) - Number(b.profit || 0);
      }

      return Number(b.cpa || 0) - Number(a.cpa || 0);
    })[0]
  : null;

  const platformColumns = getPlatformColumns();
  const getTrendConfig = () => {
    switch (normalizedReportType) {
      case 'sales_campaign':
        return {
          title: 'Revenue vs Purchases',
          firstKey: 'revenue',
          firstLabel: 'Revenue',
          secondKey: 'conversions',
          secondLabel: 'Purchases',
          tableSecond: 'Revenue',
          tableThird: 'Purchases',
        };

      case 'lead_generation':
        if (!hasClickMetrics) {
          return {
            title: 'Impressions vs Leads',
            firstKey: 'impressions',
            firstLabel: 'Impressions',
            secondKey: 'conversions',
            secondLabel: 'Leads',
            tableSecond: 'Impressions',
            tableThird: 'Leads',
          };
        }

        return {
          title: 'Clicks vs Leads',
          firstKey: 'clicks',
          firstLabel: 'Clicks',
          secondKey: 'conversions',
          secondLabel: 'Leads',
          tableSecond: 'Clicks',
          tableThird: 'Leads',
        };

      case 'sales_data':
        return {
          title: 'Revenue vs Orders',
          firstKey: 'revenue',
          firstLabel: 'Revenue',
          secondKey: 'orders',
          secondLabel: 'Orders',
          tableSecond: 'Revenue',
          tableThird: 'Orders',
        };

      default:
        return {
          title: 'Clicks vs Conversions',
          firstKey: 'clicks',
          firstLabel: 'Clicks',
          secondKey: 'conversions',
          secondLabel: 'Conversions',
          tableSecond: 'Clicks',
          tableThird: 'Conversions',
        };
    }
  };

  const trendConfig = getTrendConfig();

  const getAiConfig = () => {
    if (normalizedReportType === 'sales_campaign') {
      return {
        title: 'Sales Campaign Insights',
        subtitle: 'AI analysis focused on revenue, purchases, ROAS, and cost per purchase',
        summaryTitle: 'Sales Performance Summary',
        recommendationTitle: 'Sales Campaign Recommendations',
      };
    }

    if (normalizedReportType === 'lead_generation') {
      return {
        title: 'Lead Generation Insights',
        subtitle: 'AI analysis focused on leads, cost per lead, CTR, and inquiry quality',
        summaryTitle: 'Lead Performance Summary',
        recommendationTitle: 'Lead Generation Recommendations',
      };
    }

    if (normalizedReportType === 'sales_data') {
      return {
        title: 'Sales Data Insights',
        subtitle: 'AI analysis focused on revenue, orders, profit, margin, and AOV',
        summaryTitle: 'Sales Data Summary',
        recommendationTitle: 'Sales Growth Recommendations',
      };
    }

    return {
      title: 'AI Marketing Insights',
      subtitle: 'AI-powered campaign analysis and recommendations',
      summaryTitle: 'Performance Summary',
      recommendationTitle: 'AI Recommendations',
    };
  };

  const aiConfig = getAiConfig();

const handleSaveEmailSettings = async () => {
  if (!canUseEmailReports) {
    return toast.error('Email Reports are available on Pro and Agency plans');
  }

  try {
    setSavingEmailSettings(true);

    const saved = await saveEmailSettings(id, emailSettings);

    setEmailSettings(saved);

    toast.success('Email settings saved');
  } catch {
    toast.error('Failed to save email settings');
  } finally {
    setSavingEmailSettings(false);
  }
};

const handleSendTestEmail = async () => {
  if (!canUseEmailReports) {
    return toast.error('Email Reports are available on Pro and Agency plans');
  }

  if (!emailSettings.recipient_email) {
    return toast.error('Recipient email required');
  }

  try {
    setSendingTestEmail(true);
    await sendTestEmail({
      recipient_email: emailSettings.recipient_email,
      cc_email: emailSettings.cc_email,
    });
    toast.success('Test email sent');
  } catch {
    toast.error('Failed to send test email');
  } finally {
    setSendingTestEmail(false);
  }
};

const handleSendMonthlyEmail = async () => {
  if (!canUseEmailReports) {
    return toast.error('Email Reports are available on Pro and Agency plans');
  }

  if (!emailSettings.recipient_email) {
    return toast.error('Recipient email required');
  }

  try {
    setSendingMonthlyEmail(true);
    const result = await sendMonthlyReport(id);
    if (result?.report_month) {
      setEmailSettings((prev) => ({
        ...prev,
        last_sent_month: result.report_month,
      }));
    }
    toast.success('Previous month report email sent');
  } catch (error) {
    toast.error(error.response?.data?.error || 'Failed to send monthly report email');
  } finally {
    setSendingMonthlyEmail(false);
  }
};

const handleConnectGmail = async () => {
  if (!canUseEmailReports) {
    return toast.error('Email Reports are available on Pro and Agency plans');
  }

  try {
    setGmailConnecting(true);
    const result = await connectGmail({
      clientId: id,
      returnTo: `/clients/${id}`,
    });

    if (!result?.url) {
      throw new Error('Missing Google OAuth URL');
    }

    window.location.href = result.url;
  } catch {
    toast.error('Failed to start Gmail connection');
    setGmailConnecting(false);
  }
};

const handleDisconnectGmail = async () => {
  try {
    setGmailConnecting(true);
    await disconnectGmail();
    setGmailConnection({ connected: false, email: null, provider: 'smtp' });
    toast.success('Gmail disconnected');
  } catch {
    toast.error('Failed to disconnect Gmail');
  } finally {
    setGmailConnecting(false);
  }
};

  if (!client && !loading) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <p>Client not found</p>
      <button className="btn btn-primary" onClick={() => navigate('/clients')}>Back to Clients</button>
    </div>
  );


  const TABS = [
    'overview',
    'trends',
    'campaigns',
    'platforms',
    ...(canUseEmailReports ? ['email reports'] : []),
    'ai insights',

  ];
  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/clients')}><ArrowLeft size={15} /></button>
        <div style={{ flex: 1 }}>
          <div className="page-title">{client?.name || '...'}</div>
         <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
           {metricConfig.title}
         </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-select" style={{ width: 'auto', fontSize: 13 }} value={platform} onChange={e => setPlatform(e.target.value)}>
            {PLATFORMS.map(p => <option key={p} value={p}>{p === 'all' ? 'All Platforms' : p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--bg3)', padding: 4, borderRadius: 10, width: 'fit-content' }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
            background: activeTab === tab ? 'var(--bg2)' : 'transparent',
            color: activeTab === tab ? 'var(--text)' : 'var(--text2)',
            boxShadow: activeTab === tab ? 'var(--shadow)' : 'none',
            transition: 'all .15s', textTransform: 'capitalize',
          }}>
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
          {[1, 2, 3, 4].map(i => <div key={i} className="card card-pad"><div className="skeleton" style={{ height: 80 }} /></div>)}
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
             {/* KPI Cards */}
             <div className="grid grid-4" style={{ marginBottom: 20 }}>
               {visibleOverviewCards.map((key) => (
                 <MetricCard
                   key={key}
                   label={metricConfig.labels[key] || key}
                   value={formatMetric(key, getCurrentValue(key))}
                   icon={
                     key === 'spend'
                       ? DollarSign
                       : key === 'clicks'
                       ? MousePointerClick
                       : key === 'conversions' || key === 'orders'
                       ? Target
                       : TrendingUp
                   }
                   color={
                     key === 'spend'
                       ? '#2563EB'
                       : key === 'revenue' || key === 'roas' || key === 'profit'
                       ? '#16A34A'
                       : key === 'cpa' || key === 'refunds'
                       ? '#DC2626'
                       : '#7C3AED'
                   }
                   change={comparison?.comparison?.[key]?.change}
                  changeType={['cpc', 'cpa', 'refunds'].includes(key) ? 'negative-good' : 'positive-good'}
                 />
               ))}
             </div>


              {/* MoM Comparison */}
              {comparison && (
                <div className="card card-pad">
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Month-over-Month Comparison</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Metric</th>
                          <th>Previous Month</th>
                          <th>Current Month</th>
                          <th>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleOverviewCards.map((key) => ({
                           key,
                           label: metricConfig.labels[key] || key,
                           fmt: (value) => formatMetric(key, value),
                         })).map(({ key, label, fmt: f }) => {
                          const d = comparison.comparison[key];
                          if (!d) return null;
                          const hasPreviousData = d.hasPreviousData !== false && d.change !== null && Number.isFinite(Number(d.change));
                          const numericChange = hasPreviousData ? Number(d.change) : 0;
                         const isNeutral = !hasPreviousData || numericChange === 0 || key === 'spend';
                         const positive = isNeutral ? null : isPositiveChange(key, numericChange);

                          const changeLabel = !hasPreviousData
                            ? 'No previous data'
                            : Math.abs(numericChange) > 300
                            ? numericChange > 0
                              ? 'High increase'
                              : 'High decrease'
                            : `${numericChange > 0 ? '+' : ''}${fmt(numericChange, 1)}%`;
                          return (
                            <tr key={key}>
                              <td style={{ fontWeight: 600 }}>{label}</td>
                              <td style={{ color: 'var(--text2)' }}>{f(d.previous)}</td>
                              <td style={{ fontWeight: 600 }}>{f(d.current)}</td>
                              <td>
                                <span style={{
                                  padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                  background: isNeutral ? 'var(--bg3)' : positive ? 'var(--success-light)' : 'var(--danger-light)',
                                  color: isNeutral ? 'var(--text3)' : positive ? 'var(--success)' : 'var(--danger)',
                                }}>
                                  {changeLabel}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

           {/* Campaign Summary */}
                {campaigns.length > 0 && (
                  <div className="card card-pad" style={{ marginTop: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
                      Campaign Summary
                    </div>

                    <div className="grid grid-4" style={{ gap: 12 }}>
                      <MetricCard
                        label="Campaigns"
                        value={campaigns.length}
                        icon={Target}
                        color="#2563EB"
                      />

                      <MetricCard
                        label="Total Spend"
                        value={fmtReportCur(campaignSummarySpend)}
                        icon={DollarSign}
                        color="#16A34A"
                      />

                      <MetricCard
                        label={metricConfig.primaryMetricLabel}
                        value={fmt(campaignSummaryConversions)}
                        icon={Target}
                        color="#7C3AED"
                      />

                      {(normalizedReportType === 'sales_data' || hasRoasMetric) && (
                        <MetricCard
                          label={metricConfig.roasLabel}
                          value={
                            normalizedReportType === 'sales_data'
                              ? fmtPct(
                                  campaigns.reduce((sum, c) => sum + Number(c.margin || 0), 0) /
                                    Math.max(campaigns.length, 1)
                                )
                              : `${fmt(campaignSummaryRoas, 2)}x`
                          }
                          icon={TrendingUp}
                          color="#F59E0B"
                        />
                      )}
                    </div>
                  </div>
                )}

            </div>
          )}



          {/* Trends Tab */}
          {activeTab === 'trends' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {trends.length === 0 ? (
                <div className="card card-pad" style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
                  No trend data available. Upload reports to see monthly trends.
                </div>
              ) : (
                <>
                  <div className="card card-pad">
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Monthly Spend Trend</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={trends}>
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                       <YAxis
                         tickFormatter={(v) =>
                           `${currencySymbol}${Math.round(v / 1000)}k`
                         }
                       />

                       <Tooltip
                         content={<Tooltip_ prefix={`${currencySymbol} `} />}
                       />
                        <Line type="monotone" dataKey="spend" stroke="#2563EB" strokeWidth={2.5} dot={{ fill: '#2563EB', r: 3 }} name="Spend" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="grid grid-2">
                    <div className="card card-pad">
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>{trendConfig.title}</div>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={trends}>
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip content={<Tooltip_ />} />
                         <Bar
                           dataKey={trendConfig.firstKey}
                           fill="#7C3AED"
                           name={trendConfig.firstLabel}
                           radius={[3, 3, 0, 0]}
                         />
                        <Bar
                          dataKey={trendConfig.secondKey}
                          fill="#059669"
                          name={trendConfig.secondLabel}
                          radius={[3, 3, 0, 0]}
                        />
                          <Legend iconSize={10} iconType="circle" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="card card-pad">
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Monthly Data Table</div>
                      <div style={{ overflowX: 'auto' }}>
                        <table>
                          <thead>
                            <tr><th>Month</th><th>Spend</th>
                                              <th>{trendConfig.tableSecond}</th>
                                              <th>{trendConfig.tableThird}</th></tr>
                          </thead>
                          <tbody>
                            {trends.map(row => (
                              <tr key={row.month}>
                                <td style={{ fontWeight: 600, fontSize: 12 }}>{row.month}</td>
                                <td>{fmtReportCur(row.spend)}</td>
                                <td>
                                  {trendConfig.firstKey === 'revenue'
                                    ? fmtReportCur(row.revenue)
                                    : fmt(row[trendConfig.firstKey])}
                                </td>

                                <td>
                                  {trendConfig.secondKey === 'revenue'
                                    ? fmtReportCur(row.revenue)
                                    : fmt(row[trendConfig.secondKey])}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}


          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <div className="card">
           <div className="card-pad" style={{ borderBottom: '1px solid var(--border)' }}>


                <div style={{ fontWeight: 700, fontSize: 14 }}>Campaign Performance</div>

              </div>

               {campaigns.length === 0 ? (
                <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text3)' }}>
                  No campaign data. Upload reports with campaign names to see breakdown.
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        {campaignColumns.map((col) => (
                          <th key={col.key}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {campaigns.map((c, i) => (
                        <tr key={i}>
                          {campaignColumns.map((col) => (
                            <td key={col.key} style={col.key === 'name' ? { maxWidth: 260 } : undefined}>
                              {col.key === 'name' ? (
                                <span className="truncate" style={{ display: 'block', fontWeight: 600 }}>
                                  {col.format(c)}
                                </span>
                              ) : (
                                <span style={{ fontWeight: ['spend', 'revenue', 'profit', 'roas'].includes(col.key) ? 700 : 500 }}>
                                  {col.format(c)}
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

            {/* Platforms Tab */}
            {activeTab === 'platforms' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                <div className="grid grid-3" style={{ gap: 16 }}>
                  <div className="card card-pad">
                    <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 700 }}>
                      ACTIVE PLATFORMS
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, marginTop: 8 }}>
                      {platforms.length}
                    </div>
                  </div>

                 <div className="card card-pad">
                   <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 700 }}>
                     TOP SPEND PLATFORM
                   </div>

                   <div
                     style={{
                       fontSize: 22,
                       fontWeight: 800,
                       marginTop: 8,
                       textTransform: 'capitalize',
                     }}
                   >
                     {topPlatform?.platform || 'N/A'}
                   </div>

                   <div
                     style={{
                       marginTop: 8,
                       fontSize: 13,
                       color: 'var(--text3)',
                     }}
                   >
                     {topPlatform ? fmtReportCur(topPlatform.spend) : 'INR 0.00'}
                   </div>
                 </div>


                  <div className="card card-pad">
                    <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 700 }}>
                      TOTAL PLATFORM SPEND
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 8 }}>
                      {fmtReportCur(totalPlatformSpend)}
                    </div>
                  </div>
                </div>

               <div className="card card-pad">
                 <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
                   Platform Summary
                 </div>

                 {platforms.length === 0 ? (
                   <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>
                     No platform data available
                   </div>
                 ) : (
                   <div
                     style={{
                       display: 'grid',
                       gridTemplateColumns: '1.2fr 2fr',
                       gap: 20,
                       alignItems: 'stretch',
                     }}
                   >
                     <div
                       style={{
                         padding: 20,
                         borderRadius: 12,
                         background: '#EFF6FF',
                         border: '1px solid #BFDBFE',
                       }}
                     >
                       <div
                         style={{
                           fontSize: 18,
                           fontWeight: 800,
                           textTransform: 'capitalize',
                           marginBottom: 8,
                         }}
                       >
                         {topPlatform?.platform || platforms[0]?.platform || 'N/A'}
                       </div>

                       <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>
                         {platforms.length === 1
                           ? normalizedReportType === 'sales_campaign'
                             ? 'This report contains sales campaign data from one platform.'
                             : normalizedReportType === 'lead_generation'
                             ? 'This report contains lead generation data from one platform.'
                             : normalizedReportType === 'sales_data'
                             ? 'This report contains sales data from one channel.'
                             : 'This report contains tracked performance from one platform.'
                           : 'This report contains data from multiple platforms.'}
                       </div>
                     </div>

                     <div className="grid grid-4" style={{ gap: 12 }}>
                       {platformColumns
                         .filter((col) => col.key !== 'platform')
                         .slice(0, 4)
                         .map((col) => (
                           <div
                             key={col.key}
                             style={{
                               padding: 16,
                               borderRadius: 12,
                               border: '1px solid var(--border)',
                               background: 'var(--bg2)',
                             }}
                           >
                             <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700 }}>
                               {col.label.toUpperCase()}
                             </div>

                             <div style={{ fontSize: 16, fontWeight: 800, marginTop: 8 }}>
                               {col.format(topPlatform || platforms[0] || {})}
                             </div>
                           </div>
                         ))}
                     </div>
                   </div>
                 )}
               </div>

                <div className="card card-pad">
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
                    Platform Breakdown
                  </div>

                  <table>
                    <thead>
                      <tr>
                        {platformColumns.map((col) => (
                          <th key={col.key}>{col.label}</th>
                        ))}
                        <th>Share</th>
                      </tr>
                    </thead>

                    <tbody>
                      {platforms.map((p, i) => {
                        const spend = parseFloat(p.spend || 0);
                        const share =
                          totalPlatformSpend > 0 ? (spend / totalPlatformSpend) * 100 : 0;

                        return (
                          <tr key={i}>
                            {platformColumns.map((col) => (
                              <td key={col.key}>
                                {col.key === 'platform' ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: COLORS[i % COLORS.length],
                                      }}
                                    />
                                    <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                                      {col.format(p)}
                                    </span>
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      fontWeight: ['spend', 'revenue', 'profit', 'roas'].includes(col.key)
                                        ? 700
                                        : 500,
                                    }}
                                  >
                                    {col.format(p)}
                                  </span>
                                )}
                              </td>
                            ))}
                            <td>{fmt(share, 1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </div>

            )}

{/* Email Reports Tab */}
{activeTab === 'email reports' && (!canUseEmailReports ? (
  <div className="card card-pad">
    <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>
      Email Reports
    </div>
    <div style={{ color: 'var(--text3)' }}>
      Email Reports are available on Pro and Agency plans.
    </div>
  </div>
) : (
  <div className="card card-pad">
    <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>
      Monthly Email Reports
    </div>

    <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 22 }}>
      Automatically email this client's monthly PDF report after each month ends.
    </div>

    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        padding: 14,
        borderRadius: 12,
        background: gmailConnection.connected ? '#ECFDF5' : '#FFF7ED',
        border: `1px solid ${gmailConnection.connected ? '#A7F3D0' : '#FED7AA'}`,
        marginBottom: 18,
      }}
    >
      <div>
        <div style={{ fontWeight: 800, fontSize: 13 }}>
          Sender Email
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
          {gmailConnection.connected
            ? `Emails will be sent from ${gmailConnection.email}.`
            : 'Connect Gmail to send emails from your login/company Gmail address. Until then, emails use the central sender with replies going to your login email.'}
        </div>
      </div>

      {gmailConnection.connected ? (
        <button
          className="btn btn-secondary"
          disabled={gmailConnecting}
          onClick={handleDisconnectGmail}
        >
          {gmailConnecting ? 'Disconnecting...' : 'Disconnect Gmail'}
        </button>
      ) : (
        <button
          className="btn btn-primary"
          disabled={gmailConnecting}
          onClick={handleConnectGmail}
        >
          {gmailConnecting ? 'Connecting...' : 'Connect Gmail'}
        </button>
      )}
    </div>

    <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
      <input
        type="checkbox"
        checked={Boolean(emailSettings.enabled)}
        onChange={(e) =>
          setEmailSettings((prev) => ({
            ...prev,
            enabled: e.target.checked,
          }))
        }
      />
      <span style={{ fontWeight: 700 }}>Enable automatic monthly reports</span>
    </label>

    <div className="grid grid-3" style={{ gap: 16 }}>
      <div>
        <label className="form-label">Recipient Email</label>
        <input
          className="form-input"
          type="email"
          value={emailSettings.recipient_email || ''}
          onChange={(e) =>
            setEmailSettings((prev) => ({
              ...prev,
              recipient_email: e.target.value,
            }))
          }
          placeholder="client@example.com"
        />
      </div>

      <div>
        <label className="form-label">CC Email</label>
        <input
          className="form-input"
          type="email"
          value={emailSettings.cc_email || ''}
          onChange={(e) =>
            setEmailSettings((prev) => ({
              ...prev,
              cc_email: e.target.value,
            }))
          }
          placeholder="optional@example.com"
        />
      </div>

      <div>
        <label className="form-label">Send Day</label>
        <select
          className="form-select"
          value={emailSettings.send_day || 1}
          onChange={(e) =>
            setEmailSettings((prev) => ({
              ...prev,
              send_day: Number(e.target.value),
            }))
          }
        >
          {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div
      style={{
        marginTop: 20,
        padding: 14,
        borderRadius: 12,
        background: '#EFF6FF',
        border: '1px solid #BFDBFE',
        fontSize: 13,
        color: 'var(--text2)',
        lineHeight: 1.6,
      }}
    >
      Example: if send day is 1 and today is July 1, the system will email the June report.
      Current incomplete month is never sent automatically.
    </div>

    <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
      <button
        className="btn btn-secondary"
        disabled={sendingTestEmail || savingEmailSettings || sendingMonthlyEmail}
        onClick={handleSendTestEmail}
      >
        {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
      </button>

      <button
        className="btn btn-primary"
        disabled={savingEmailSettings || sendingTestEmail || sendingMonthlyEmail}
        onClick={handleSaveEmailSettings}
      >
        {savingEmailSettings ? 'Saving...' : 'Save Settings'}
      </button>

      <button
        className="btn btn-secondary"
        disabled={sendingMonthlyEmail || savingEmailSettings || sendingTestEmail}
        onClick={handleSendMonthlyEmail}
      >
        {sendingMonthlyEmail ? 'Sending...' : 'Send Previous Month Now'}
      </button>
    </div>
  </div>
))}

             {/* AI Insights Tab */}
{activeTab === 'ai insights' && (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

    <div className="card card-pad">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
        }}
      >
        <div>
          <div style={{ fontWeight: 800, fontSize: 18 }}>
            {aiConfig.title}
          </div>

          <div
            style={{
              fontSize: 13,
              color: 'var(--text3)',
              marginTop: 4,
            }}
          >
            {aiConfig.subtitle}
          </div>
        </div>

        <button
          className="btn btn-primary"
          disabled={aiLoading}
          onClick={async () => {
            try {
              setAiLoading(true);

              const result = await generateAIInsights(id);

              setAiInsight(result);

              toast.success('AI insights generated');
            } catch {
              toast.error('Failed to generate AI insights');
            } finally {
              setAiLoading(false);
            }
          }}
        >
          {aiLoading ? 'Generating...' : `Generate ${aiConfig.title}`}
        </button>
      </div>

      {!aiInsight ? (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            color: 'var(--text3)',
          }}
        >
          No AI insights generated yet.
        </div>
      ) : (
        <>
          <div
            style={{
              padding: 18,
              borderRadius: 14,
              background: 'linear-gradient(135deg,#EEF2FF,#F8FAFC)',
              border: '1px solid #C7D2FE',
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: 15,
                marginBottom: 10,
              }}
            >
              {aiConfig.summaryTitle}
            </div>

            <div
              style={{
                fontSize: 14,
                color: 'var(--text2)',
                lineHeight: 1.7,
              }}
            >
              {aiInsight.summary}
            </div>
          </div>

<div
  style={{
    fontSize: 11,
    color: 'var(--text3)',
    marginBottom: 16,
  }}
>
  Generated on:{' '}
  {aiInsight.created_at
    ? new Date(aiInsight.created_at).toLocaleString()
    : 'Recently'}
</div>

          <div>
            <div
              style={{
                fontWeight: 800,
                fontSize: 15,
                marginBottom: 14,
              }}
            >
             {aiConfig.recommendationTitle}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {(Array.isArray(aiInsight.recommendations)
                ? aiInsight.recommendations
                : JSON.parse(aiInsight.recommendations || '[]')
              ).map((rec, index) => (
                <div
                  key={index}
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    border: '1px solid var(--border)',
                    background: 'var(--bg2)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        minWidth: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: '#DBEAFE',
                        color: '#2563EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {index + 1}
                    </div>

                    <div
                      style={{
                        fontSize: 14,
                        color: 'var(--text2)',
                        lineHeight: 1.7,
                      }}
                    >
                      {rec}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  </div>
)}

{/* Integrations Tab */}
            </>
          )}
        </div>
      );
    }
